import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Eraser } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Signature pad with auto-upload to public storage.
 * onUploaded(fileId) is called once the signed PNG is stored.
 */
export default function SignaturePad({ onUploaded, label = "Sign here" }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [fileId, setFileId] = useState(null);

  const clear = () => { ref.current?.clear(); setFileId(null); onUploaded?.(null); };

  const save = async () => {
    if (!ref.current || ref.current.isEmpty()) {
      toast.error("Please sign first");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = ref.current.getTrimmedCanvas().toDataURL("image/png");
      const blob = await (await fetch(dataUrl)).blob();
      const form = new FormData();
      form.append("file", blob, "signature.png");
      const { data } = await api.post("/files/upload-public?folder=signatures", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFileId(data.file_id);
      onUploaded?.(data.file_id);
      toast.success("Signature saved");
    } catch (e) {
      toast.error("Could not save signature");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="signature-pad">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-ink">{label}</div>
        <button type="button" onClick={clear} className="text-xs text-ink-soft hover:text-terracotta flex items-center gap-1" data-testid="signature-clear">
          <Eraser weight="duotone" size={14} /> Clear
        </button>
      </div>
      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        <SignatureCanvas
          ref={ref}
          penColor="#2D2926"
          canvasProps={{ className: "w-full h-40 cursor-crosshair touch-none", "data-testid": "signature-canvas" }}
        />
      </div>
      <button
        type="button"
        onClick={save}
        disabled={uploading}
        className={`w-full h-10 rounded-full text-sm font-semibold transition ${
          fileId ? "bg-sage-deep text-white" : "bg-ink text-white hover:bg-black/90"
        } disabled:opacity-60`}
        data-testid="signature-save"
      >
        {uploading ? "Saving..." : fileId ? "✓ Signature saved" : "Save signature"}
      </button>
    </div>
  );
}
